'use client';

import { Heading } from '@/components/cms/Heading';
import { useAriaLabels } from '@/hooks/useAriaLabels';
import { Link } from '@/lib/i18n/navigation';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';

import {
  buildBreadcrumbJsonLd,
  type BreadcrumbItem,
  type BreadcrumbsPageHeaderProps,
} from './Breadcrumbs.shared';
import { BreadcrumbsInline } from './BreadcrumbsInline';
import styles from '../Breadcrumbs.module.scss';

function buildPageHeaderTrail(
  items: BreadcrumbItem[] | undefined,
  homeLabel: string,
  homeHref: string
): BreadcrumbItem[] {
  return items?.length ? items : [{ label: homeLabel, href: homeHref }];
}

function buildPageHeaderJsonLd(trail: BreadcrumbItem[], title: string): BreadcrumbItem[] {
  const lastItem = trail.at(-1);
  return lastItem?.label === title ? trail : [...trail, { label: title }];
}

/**
 * Page-header breadcrumb with a large title below the trail.
 * Used on checkout, order confirmation, and service flows.
 */
export const BreadcrumbsPageHeader = ({
  title,
  items,
  headingType = 'h1',
  headingClassName,
  homeHref = '/',
  noPadding = false,
  className,
  testId = 'Breadcrumbs',
  appearance = 'default',
}: BreadcrumbsPageHeaderProps) => {
  const aria = useAriaLabels();
  const t = useTranslations('breadcrumbs');
  const homeLabel = t('home');
  const trail = buildPageHeaderTrail(items, homeLabel, homeHref);
  const jsonLd = buildBreadcrumbJsonLd(buildPageHeaderJsonLd(trail, title));
  const resolvedAppearance = appearance ?? (items?.length ? 'cta' : 'default');
  const useInlineTrail = resolvedAppearance === 'cta' || Boolean(items?.length);

  return (
    <header
      className={clsx(styles.pageHeader, noPadding && styles.pageHeaderNoPadding, className)}
      data-testid={testId}
    >
      {useInlineTrail ? (
        <BreadcrumbsInline
          items={trail}
          className={styles.pageHeaderTrail}
          testId={`${testId}-trail`}
          includeJsonLd={false}
          appearance={resolvedAppearance === 'cta' ? 'cta' : 'default'}
        />
      ) : (
        <nav className={styles.pageHeaderBreadcrumb} aria-label={aria.action('breadcrumb')}>
          <Link
            href={homeHref}
            className={styles.pageHeaderHomeLink}
            data-testid={`${testId}-home`}
          >
            {homeLabel}
          </Link>
          <span className={styles.pageHeaderSeparator} aria-hidden="true">
            {' '}
            /
          </span>
        </nav>
      )}
      <Heading
        Heading={{
          text: title,
          headingType,
          headingSeoTag: 'h1',
          textAlignment: 'left',
          mainColor: '#000000',
        }}
        className={clsx(styles.pageHeaderHeading, headingClassName)}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
    </header>
  );
};

BreadcrumbsPageHeader.displayName = 'BreadcrumbsPageHeader';
